import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EmailVerificationModal({ isOpen, onClose }: EmailVerificationModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [resending, setResending] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      await apiRequest("POST", "/api/auth/resend-verification");
      toast({
        title: "Verification Email Sent",
        description: "Please check your inbox and click the link to verify your email.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to resend. Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="modal-email-verification">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-5 w-5 text-blue-600" />
            <DialogTitle>Verify Your Email First</DialogTitle>
          </div>
          <DialogDescription>
            This action requires a verified email address.
            {user?.email && (
              <span className="block mt-1">
                We sent a verification link to <strong>{user.email}</strong>.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Check your inbox and click the link to unlock posting and messaging.
          If you didn't receive it, click below to resend.
        </p>

        <div className="flex gap-3 mt-2">
          <Button
            onClick={handleResend}
            disabled={resending}
            data-testid="button-resend-verification-modal"
          >
            {resending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "Resend Verification Email"
            )}
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="button-close-verification-modal">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
